import React, { useState, useCallback, useEffect, useRef } from 'react'
import { LoginBox } from '@/components/auth/loginBox'
import { useLocalization } from '@/hooks/localization'
import context from '@/context'
import LoadingIndicator from '@/components/LoadingIndicator'
import { parseQueryString } from '@/utils/url'
import { Link } from 'react-router-dom'

const ResetPasswordPage = React.memo(function ForgotPasswordPage ({ location }) {
  const qs = parseQueryString(location.search)
  const [verified, setVerified] = useState(null)
  const [submitted, setSubmitted] = useState(false)
  const [waiting, setWaiting] = useState(false)
  const [failure, setFailure] = useState('')
  const resetPassword = useRef()
  const confirmPassword = useRef()
  const { localize } = useLocalization()
  const onSubmit = useCallback(() => {
    setWaiting(true)
    context.api.auth.resetPassword({ email: qs.email, token: qs.token, resetPassword: resetPassword.current.value, confirmPassword: confirmPassword.current.value })
      .then(() => {
        setSubmitted(true)
        setWaiting(false)
      })
      .catch((e) => {
        setWaiting(false)
        setFailure(getFailure(e))
      })
  }, [qs.email, qs.token])
  useEffect(() => {
    setSubmitted(false)
    setWaiting(false)
    context.api.auth.verifyResetToken({ email: qs.email, token: qs.token })
      .then(setVerified)
      .catch((e) => {
        setVerified({ status: e.response.status, message: ((e.response.data || {}).error || {}).message })
      })
  }, [qs.email, qs.token])

  if (submitted) {
    return (
      <LoginBox title='Security.ResetPasswordConfirmationTitle' smallTitle>
        <p className='text-center'>
          <strong>{localize('Security.ResetPasswordConfirmation')}</strong>
          <Link to='/login'>{localize('Security.ResetPasswordConfirmationLogin')}</Link>
        </p>
      </LoginBox>
    )
  }

  if (verified === null) {
    return (
      <LoginBox title='Security.ForgotPasswordConfirmationTitle' smallTitle>
        <LoadingIndicator />
      </LoginBox>
    )
  }

  if (verified !== true) {
    return (
      <LoginBox title='Security.InvalidTokenTitle' smallTitle>
        <p className='text-center'>
          <strong>{localize(getFailureText(verified))}</strong> <Link to='/login/forgot'>{localize('Security.ForgotPassword')}</Link>
        </p>
      </LoginBox>
    )
  }
  const reason = (qs.reason ? localize(qs.reason) : null)
  return (
    <LoginBox title='Security.ResetPasswordTitle' smallTitle>
      <fieldset>
        {/* hack for firefox version < 67.0.0 and other browsers that ignore autocomplete:new-password */}
        <input type='password' className='hidden' />
        {reason && <div className='form-group'>
          <label>{reason}</label>
        </div>}
        <div className='form-group'>
          <label htmlFor='ResetPassword'>{localize('Security.Password')}</label>
          <input className='form-control' placeholder={localize('Security.Password')} autoComplete='new-password' type='password' id='ResetPassword' ref={resetPassword} disabled={waiting} autoFocus />
        </div>
        <div className='form-group'>
          <label htmlFor='ConfirmPassword'>{localize('Security.ConfirmPassword')}</label>
          <input className='form-control' placeholder={localize('Security.ConfirmPassword')} id='ConfirmPassword' type='password' ref={confirmPassword} disabled={waiting} />
        </div>
        {!!failure && <div className='alert alert-danger'>
          <div className='danger validation-summary-errors'>
            {failure.map(x => x.localized ? x.message : localize(x.message)).reduce((prev, curr) => [prev, <br />, curr])}
          </div>
        </div>}
        <div className='form-group d-flex justify-content-between'>
          <button className='btn btn-primary' name='button' value='submit' type='submit' onClick={onSubmit} disabled={waiting}>{localize('Common.Submit')}</button>
        </div>
      </fieldset>
    </LoginBox>
  )
})

function getFailure (e) {
  switch (e.response.status) {
    case 400: {
      const localized = e.response.data.error.message === 'ValidationError'
      return e.response.data.error.validationErrors.map(({ message }) => ({ message, localized }))
    }
    default: return [{ message: 'Common.ServiceUnavailable' }]
  }
}

function getFailureText ({ status, message }) {
  switch (status) {
    case 502: return 'Common.ServiceUnavailable'
    default: return message || 'Security.InvalidToken'
  }
}

export {
  ResetPasswordPage
}



// WEBPACK FOOTER //
// ./src/components/auth/resetPassword.js